/**
 * CARC (Claim Adjustment Reason Codes) and RARC (Remittance Advice Remark Codes)
 * Standard X12 healthcare codes for explaining payment adjustments
 *
 * Sources:
 * - X12 CARC: https://x12.org/codes/claim-adjustment-reason-codes
 * - CMS RARC: https://www.cms.gov/Medicare/Billing/ElectronicBillingEDITrans/Remittance-Advice-Remark-Codes
 */

/**
 * Claim Adjustment Group Codes
 * These indicate who is financially responsible for the adjustment
 */
export const GROUP_CODES: Record<string, string> = {
  CO: "Contractual Obligation - Provider write-off per contract",
  CR: "Correction/Reversal - Correction to prior claim",
  OA: "Other Adjustment - Not classifiable to other groups",
  PI: "Payer Initiated Reduction - Payer policy adjustment",
  PR: "Patient Responsibility - Patient owes this amount",
};

/**
 * CARC - Claim Adjustment Reason Codes
 * Explain why a claim or service line was adjusted
 */
export const CARC_CODES: Record<string, string> = {
  // Deductible, Coinsurance, Copay (most common PR codes)
  "1": "Deductible amount",
  "2": "Coinsurance amount",
  "3": "Co-payment amount",

  // Contractual/Fee Schedule (most common CO codes)
  "4": "The procedure code is inconsistent with the modifier used or a required modifier is missing",
  "5": "The procedure code/type of bill is inconsistent with the place of service",
  "8": "The procedure code is inconsistent with the provider type/specialty",
  "16": "Claim/service lacks information or has submission/billing error(s)",
  "18": "Exact duplicate claim/service",
  "22": "This care may be covered by another payer per coordination of benefits",
  "23": "Payment adjusted due to the impact of prior payer(s) adjudication",
  "45": "Charge exceeds fee schedule/maximum allowable or contracted/legislated fee arrangement",
  "59": "Multiple surgery reduction - additional procedure(s) paid at reduced rate",
  "96": "Non-covered charge(s) - patient not liable per contract",
  "97": "Payment included in the allowance for another service/procedure",
  "119": "Benefit maximum for this time period or occurrence has been reached",
  "197": "Precertification/authorization/notification/pre-treatment absent",

  // Additional common codes from your data
  "109":
    "Claim/service not covered by this payer/contractor - submit to correct payer/contractor",
  "204":
    "This service/equipment/drug is not covered under the patient's current benefit plan",
  "236":
    "This procedure or procedure/modifier combination is not compatible with another procedure performed on the same day",
  "253": "Sequestration - Loss in Federal Funding",
  "272": "Coverage/program guidelines were not met",
  "273": "Coverage/program guidelines were exceeded",

  // B-series codes (Benefits)
  B10: "Allowed amount has been reduced because a component of the basic procedure/test was paid as a separate procedure",
  B13: "Previously paid - payment for this claim/service may have been paid in a previous payment",

  // P-series codes (Policy/Prior)
  P12: "Workers Compensation case settled - patient may be responsible",

  // Additional PR codes
  "150":
    "Payment adjusted because the payer deems the information submitted does not support this level of service",
  "170":
    "Payment adjusted because the claim was processed in accordance with an appeal settlement",
  "227":
    "Information requested from the patient/insured/responsible party was not provided",
  "252":
    "An attachment/other documentation is required to adjudicate this claim/service",

  // Common denial/reduction codes
  "94": "Processed in Excess of charges",
};

/**
 * RARC - Remittance Advice Remark Codes
 * Provide additional explanation for adjustments
 */
export const RARC_CODES: Record<string, string> = {
  // M-series (Medicare/Medicaid specific)
  M15: "Separately billed services/tests have been bundled as they are considered components of the same procedure",
  M47: "Claim/service spans eligible and ineligible periods of coverage",
  M77: "Missing/incomplete/invalid place of service",
  M127: "This is a contractor medical policy/technical denial",

  // MA-series (Medicare Alert)
  MA04: "Secondary payment cannot be considered without the identity of or payment information from the primary payer",
  MA109:
    "Claim processed in accordance with CMS requirements for bundled services",
  MA130: "Your claim contains incomplete/invalid data",

  // N-series (Informational)
  N19: "Procedure code incidental to primary procedure",
  N20: "Service not separately priced or reimbursed by this payer",
  N45: "Payment based on authorized amount",
  N59: "Please refer to your contract for filing/billing guidelines",
  N95: "This decision was based on a payer policy",
  N130: "Consult plan benefit documents for coverage/cost share details",
  N179: "Additional information is available upon request",
  N245: "Payment adjusted based on the interruption of treatment for this service",
  N390: "Missing/invalid indicator for attachment availability",
  N522: "Duplicate of a claim processed or in process",
  N580: "Determination based on the provisions of the insurance policy",
  N600: "Payment based on provider-submitted information",
  N657: "This service/claim payment is being delayed for additional review",
  N706: "Payment reflects a rate established by state/federal regulation",
};

/**
 * Helper function to get adjustment code description
 */
export function getAdjustmentDescription(
  group: string,
  code: string
): string | null {
  return CARC_CODES[code] || null;
}

/**
 * Helper function to get group code description
 */
export function getGroupDescription(group: string): string | null {
  return GROUP_CODES[group] || null;
}

/**
 * Helper function to get remark code description
 */
export function getRemarkDescription(code: string): string | null {
  return RARC_CODES[code] || null;
}

/**
 * Format adjustment for display (e.g., "CO-45 $3,288.34")
 */
export function formatAdjustment(
  group: string,
  code: string,
  amount: number
): string {
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
  return `${group}-${code} ${formattedAmount}`;
}
